import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase/client";
import { Search, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { AddTOTPDialog } from "@/components/AddTOTPDialog";
import { ExportTOTPButton } from "@/components/ExportTOTPButton";
import { EditTOTPDialog } from "@/components/EditTOTPDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AddUserDialog } from "@/components/AddUserDialog";
import { ExportUsersButton } from "@/components/ExportUsersButton";
import { EditUserDialog } from "@/components/EditUserDialog";
import { MaskedTOTP } from "@/components/MaskedCode";
import { generateTOTP, getTimeRemaining } from "@/lib/totp";
import type { Profile, TOTPCode, TOTPCategory } from "@/lib/supabase/client";
import { DeleteUserDialog } from "@/components/DeleteUserDialog";
import { LoasTab } from "@/components/LoasTab";

const TENYLLE_TEAM_ID = "4300398d-f705-4630-b840-70beae241d49";

interface UserTeamInfo {
  team_id: string;
  team: {
    name: string;
  };
}

interface TeamInfo {
  id: string;
  name: string;
}

type DashboardCode = TOTPCode & {
  teamsList: TeamInfo[];
  category: TOTPCategory | null;
};

export function Dashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("codes");
  const [codes, setCodes] = useState<DashboardCode[]>([]);
  const [categories, setCategories] = useState<TOTPCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [users, setUsers] = useState<Profile[]>([]);
  const [userTeams, setUserTeams] = useState<Record<string, UserTeamInfo[]>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoasTeam, setIsLoasTeam] = useState(false);
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000));
  const { toast } = useToast();

  useEffect(() => {
    // fetch("/public/manifest.webmanifest")
    // .then((res) => res.json())
    // .then((json) => console.log(json))

    const timer = setInterval(() => {
      if (currentTime) {
      }
      setCurrentTime(Math.floor(Date.now() / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!isAdmin || !user) return;
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        .order("email");
      if (profilesError) throw profilesError;
      setUsers(profiles || []);

      const { data: teamUsers, error: teamsError } = await supabase
        .from("user_teams")
        .select("user_id, team_id, teams (id, name)");

      if (teamsError) throw teamsError;

      const teamsMap: Record<string, UserTeamInfo[]> = {};
      teamUsers.forEach((ut) => {
        if (!teamsMap[ut.user_id]) {
          teamsMap[ut.user_id] = [];
        }
        if (ut.teams) {
          const teamData = Array.isArray(ut.teams) ? ut.teams[0] : ut.teams;
          if (teamData) {
            teamsMap[ut.user_id].push({
              team_id: teamData.id,
              team: { name: teamData.name },
            });
          }
        }
      });
      setUserTeams(teamsMap);
    } catch (error: any) {
      console.error("Error fetching users:", error);
      toast({
        variant: "destructive",
        title: "Erro ao buscar usuários",
        description: error.message,
      });
    }
  }, [isAdmin, user, toast]);

  const fetchCodes = useCallback(async () => {
    if (!user) return;
    try {
      let query = supabase
        .from("totp_codes")
        .select(
          "id, name, secret, team_id, created_at, created_by, category_id, teams (id, name), totp_categories (id, name, created_at)"
        )
        .order("created_at", { ascending: false }); // ordenando por data de criação mais recente

      if (!isAdmin) {
        const { data: userTeams } = await supabase
          .from("user_teams")
          .select("team_id")
          .eq("user_id", user.id);

        const teamIds = userTeams?.map((ut) => ut.team_id) || [];
        if (teamIds.length === 0) {
          setCodes([]);
          setCategories([]);
          setActiveCategory("all");
          return;
        }
        query = query.in("team_id", teamIds);
      }
      const { data, error } = await query;
      if (error) throw error;

      const codesMap = new Map<string, DashboardCode>();
      data?.forEach((code) => {
        const key = `${code.name}-${code.secret}`;
        const teamData = Array.isArray(code.teams)
          ? code.teams[0]
          : code.teams;
        const categoryData = Array.isArray(code.totp_categories)
          ? code.totp_categories[0]
          : code.totp_categories;

        if (!codesMap.has(key)) {
          codesMap.set(key, {
            id: code.id,
            name: code.name,
            secret: code.secret,
            team_id: code.team_id,
            created_at: code.created_at,
            created_by: code.created_by,
            category_id: code.category_id,
            category: categoryData ?? null,
            teamsList: teamData
              ? [{ id: teamData.id, name: teamData.name }]
              : [],
          });
        } else {
          const existingCode = codesMap.get(key)!;
          if (
            teamData &&
            !existingCode.teamsList.some((t) => t.id === teamData.id)
          ) {
            existingCode.teamsList.push({
              id: teamData.id,
              name: teamData.name,
            });
          }
          if (!existingCode.category && categoryData) {
            existingCode.category = categoryData;
            existingCode.category_id = categoryData.id;
          }
        }
      });

      const codesArray = Array.from(codesMap.values());
      setCodes(codesArray);

      const categoriesMap = new Map<string, TOTPCategory>();
      codesArray.forEach((codeItem) => {
        if (codeItem.category) {
          categoriesMap.set(codeItem.category.id, codeItem.category);
        }
      });

      const sortedCategories = Array.from(categoriesMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setCategories(sortedCategories);
      setActiveCategory((prev) => {
        if (
          prev !== "all" &&
          !categoriesMap.has(prev)
        ) {
          return "all";
        }
        return prev;
      });
    } catch (error: any) {
      console.error("Error fetching codes:", error);
      toast({
        variant: "destructive",
        title: "Erro ao buscar códigos",
        description: error.message,
      });
    }
  }, [user, isAdmin, toast]);

  useEffect(() => {
    if (!user) return;

    // Check if user is admin
    const checkAdmin = async () => {
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("metadata")
          .eq("id", user.id)
          .single();

        setIsAdmin(profile?.metadata?.is_admin || false);

        const { data: loasAccess } = await supabase
          .from("user_teams")
          .select("team_id")
          .eq("user_id", user.id)
          .eq("team_id", TENYLLE_TEAM_ID)
          .maybeSingle();
        setIsLoasTeam(!!loasAccess);
      } catch (error: any) {
        console.error("Error checking admin status:", error);
        toast({
          variant: "destructive",
          title: "Erro ao verificar o status do administrador",
          description: error.message,
        });
      }
    };

    let subscription: ReturnType<typeof supabase.channel> | null = null;

    const setupSubscription = () => {
      // Subscribe to changes
      subscription = supabase
        .channel("schema_db_changes")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "totp_codes",
          },
          () => {
            console.log("TOTP codes changed, refreshing...");
            fetchCodes();
          }
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "profiles",
          },
          () => {
            console.log("Profiles changed, refreshing...");
            fetchUsers();
          }
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "user_teams",
          },
          () => {
            console.log("User teams changed, refreshing...");
            fetchUsers();
            fetchCodes(); // Also refresh codes as team access might have changed
          }
        )
        .subscribe();
    };

    // Initial data fetch
    checkAdmin().then(() => {
      fetchCodes();
      fetchUsers();
      setupSubscription();
    });

    // Cleanup subscription on unmount
    return () => {
      if (subscription) {
        console.log("Cleaning up subscription...");
        subscription.unsubscribe();
      }
    };
  }, [user, fetchCodes, fetchUsers, toast]);

  const normalizedSearch = searchQuery.toLowerCase();
  const categoryFilteredCodes = codes.filter((code) => {
    if (activeCategory === "all") {
      return true;
    }
    return code.category_id === activeCategory;
  });
  const filteredCodes = categoryFilteredCodes.filter(
    (code) =>
      code.name.toLowerCase().includes(normalizedSearch) ||
      (code.category?.name ?? "").toLowerCase().includes(normalizedSearch)
  );

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(normalizedSearch) ||
      (user.metadata?.name ?? "").toLowerCase().includes(normalizedSearch)
  );

  return (
    <div className="min-h-screen bg-background text-foreground p-4">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex items-center justify-between notranslate" translate="no">
          <TabsList>
            <TabsTrigger
              value="codes"
            >
              Códigos TOTP
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger
                value="users"
              >
                Usuários
              </TabsTrigger>
            )}
            {isLoasTeam && (
              <TabsTrigger
                value="loas"
              >
                LOAS
              </TabsTrigger>
            )}
          </TabsList>
          {isAdmin && (
            <div className="flex items-center gap-2">
              {activeTab === "users" ? (
                <>
                  <ExportUsersButton />
                  <AddUserDialog />
                </>
              ) : (
                <>
                  <ExportTOTPButton />
                  <AddTOTPDialog />
                </>
              )}
            </div>
          )}
        </div>

        <TabsContent value="codes" className="space-y-6">
          <Tabs
            value={activeCategory}
            onValueChange={setActiveCategory}
            className="w-full"
          >
            <div className="overflow-x-auto notranslate" translate="no">
              <TabsList className="flex-nowrap gap-1 min-w-max">
                <TabsTrigger
                  value="all"
                >
                  Todos
                </TabsTrigger>
                {categories.map((category) => (
                  <TabsTrigger
                    key={category.id}
                    value={category.id}
                  >
                    {category.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </Tabs>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Buscar códigos"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-secondary border-none pl-10 text-foreground placeholder:text-muted-foreground"
            />
          </div>

          {/* TOTP Codes List */}
          <div className="space-y-4 notranslate" translate="no">
            {filteredCodes.map((code) => (
              <div
                key={code.id}
                className="bg-secondary rounded-lg p-4 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{code.name}</span>
                  <div className="flex items-center space-x-2">
                    {isAdmin && (
                      <EditTOTPDialog code={code} onUpdate={fetchCodes} />
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  {user && (
                    <MaskedTOTP
                      value={generateTOTP(code.secret)}
                      user={{ id: user.id, email: user.email ?? "" }}
                      codeId={code.id}
                      codeName={code.name}
                      className="text-3xl"
                    />
                  )}
                  <div className="h-1 w-1 rounded-full bg-primary animate-pulse" />
                </div>
                <div className="w-full bg-secondary rounded-full h-1">
                  <div
                    className="bg-primary h-1 rounded-full transition-all duration-1000 ease-linear"
                    style={{
                      width: `${(getTimeRemaining() / 30) * 100}%`,
                    }}
                  />
                </div>
                {code.category && (
                  <div className="mt-2 text-sm text-muted-foreground">
                    Categoria:{" "}
                    <span className="text-foreground">{code.category.name}</span>
                  </div>
                )}
                {code.teamsList && code.teamsList.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-sm text-muted-foreground">Times:</span>
                    {code.teamsList.map((team) => (
                      <span key={team.id} className="text-sm text-foreground">
                        {team.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="users" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                Gerenciamento de Usuários
              </h2>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar usuários"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-secondary border-none pl-10 text-foreground placeholder:text-muted-foreground"
              />
            </div>

            {/* Users List */}
            <div className="space-y-4">
              {filteredUsers.map((u) => (
                <div
                  key={u.id}
                  className="bg-secondary rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium">
                        {u.metadata?.name || "Usuário sem nome"}
                      </h3>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {u.metadata?.is_admin && (
                        <Shield className="h-5 w-5 text-primary" />
                      )}
                      <EditUserDialog user={u} onUpdate={fetchUsers} />
                      <DeleteUserDialog user={u} onUpdate={fetchUsers} />
                    </div>
                  </div>
                  {userTeams[u.id] && userTeams[u.id].length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {userTeams[u.id].map((team) => (
                        <span
                          key={team.team_id}
                          className="px-2 py-1 text-xs rounded-full bg-secondary text-foreground"
                        >
                          {team.team.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        )}

        {isLoasTeam && (
          <TabsContent value="loas" className="space-y-6">
            <LoasTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
